import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Check, Loader2, AlertCircle, MoreHorizontal } from "lucide-react"
import { useStyles, type StylePreset } from "@/hooks/useStyles"
import type { StyleId } from "@/lib/constants"
import { localizeDescription, localizeName } from "@/lib/i18n-content"
import { StyleSelectionModal } from "./StyleSelectionModal"
import { StyleThumbnail } from "./StyleThumbnail"

interface StyleSelectorProps {
  selectedStyle: StyleId
  onStyleChange: (style: StyleId) => void
  disabled?: boolean
}

export function StyleSelector({ selectedStyle, onStyleChange, disabled = false }: StyleSelectorProps) {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleStyleClick = (styleId: string) => {
    if (disabled) return
    // Toggle: 이미 선택된 스타일을 다시 클릭하면 선택 해제
    if (selectedStyle === styleId) {
      onStyleChange(null)
    } else {
      onStyleChange(styleId)
    }
  }

  const { data: styles, isLoading, error } = useStyles()

  // 동적 그리드: 선택된 스타일을 항상 표시
  const displayedStyles = useMemo(() => {
    if (!styles) return []

    // 선택된 스타일이 없거나 목록에 없는 경우
    const selectedStyleObj = selectedStyle ? styles.find(s => s.id === selectedStyle) : null
    if (!selectedStyleObj) {
      return styles.slice(0, 5)
    }

    // 선택된 스타일의 인덱스 확인
    const selectedIndex = styles.findIndex(s => s.id === selectedStyle)

    // 선택된 스타일이 처음 5개 안에 있으면 그대로 표시
    if (selectedIndex < 5) {
      return styles.slice(0, 5)
    }

    // 선택된 스타일이 5번째 이후면, 처음 4개 + 선택된 스타일
    const otherStyles = styles.filter(s => s.id !== selectedStyle).slice(0, 4)
    return [...otherStyles, selectedStyleObj]
  }, [styles, selectedStyle])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-md p-3">
        <AlertCircle className="w-4 h-4" />
        {t("generator.stylesLoadFailed")}
      </div>
    )
  }

  return (
    <>
      <div className={`grid grid-cols-3 gap-2.5 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        {displayedStyles.map((style) => (
          <StyleCard
            key={style.id}
            style={style}
            isSelected={selectedStyle === style.id}
            onSelect={() => handleStyleClick(style.id)}
            disabled={disabled}
          />
        ))}

        {/* More button */}
        <button
          onClick={() => !disabled && setIsModalOpen(true)}
          disabled={disabled}
          className="relative rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 transition-all bg-slate-50 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-slate-300 disabled:hover:bg-slate-50"
          aria-label={t("generator.viewAllStyles")}
        >
          <div className="w-full aspect-square flex flex-col items-center justify-center gap-1">
            <MoreHorizontal className="w-6 h-6 text-slate-500" />
            <span className="text-xs font-medium text-slate-600">
              {t("generator.viewAll")}
            </span>
          </div>
        </button>
      </div>

      {/* Modal for all styles */}
      {styles && (
        <StyleSelectionModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          styles={styles}
          selectedStyle={selectedStyle}
          onStyleChange={onStyleChange}
        />
      )}
    </>
  )
}

interface StyleCardProps {
  style: StylePreset
  isSelected: boolean
  onSelect: () => void
  disabled?: boolean
}

function StyleCard({ style, isSelected, onSelect, disabled = false }: StyleCardProps) {
  // localizeName/localizeDescription 결과가 언어 변경 시 다시 렌더링되도록 구독
  useTranslation()
  return (
    <button
      onClick={onSelect}
      disabled={disabled}
      title={localizeDescription(style) ?? undefined}
      className={`relative rounded-lg border-2 overflow-hidden transition-all shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
        isSelected
          ? "border-primary ring-2 ring-primary/20"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      {/* Thumbnail with overlay label */}
      <div className="w-full aspect-square bg-slate-100 relative">
        <StyleThumbnail
          src={style.thumbnail}
          updatedAt={style.updatedAt}
          alt={localizeName(style)}
          className="w-full h-full"
        />
        {/* Selected indicator - top right */}
        {isSelected && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary border-2 border-white flex items-center justify-center shadow-md">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
        {/* Label overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1.5 py-1">
          <p className="text-[12px] font-medium text-white truncate">
            {localizeName(style)}
          </p>
        </div>
      </div>
    </button>
  )
}
